import { Button, Card, Container, IconButton, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { DeviceWithLastAssetNotification, EldLandmark, Organization } from "src/models";
import useModelData, { replaceById, deleteById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { formatDatetimeForPreview } from "src/utils/date-utils";
import { useAuthContext } from "src/auth/hooks";

import SimpleDialog from "src/components/simple-dialog";
import PageHeader from "src/components/page-header";
import NoneFound from "src/components/none-found";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useParams, useRouter } from "src/routes/hooks";
import { useSearch } from "src/hooks/use-search";
import { SearchCard } from "src/components/search-card";
import DisplayErrors from "src/components/display-errors";
import { formatAddress } from "src/utils/address-utils";
import dayjs from "dayjs";

type EldDevicesProps = {
  devices?: DeviceWithLastAssetNotification[]
}

const EldDevices = memo(({ devices }: EldDevicesProps) => {
  const { sortedList, sortData } = useSort(devices);

  const {
    searchText,
    setSearchText,
    filteredList,
  } = useSearch(sortedList, ["deviceType"]);

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  if (!sortedList) {
    return null;
  }

  return <div>
    <SearchCard searchText={searchText} setSearchText={setSearchText} searchingFor="Devices" />
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell>
                Device Type
              </TruxTableCell>
              <TruxTableCell>
                Asset
              </TruxTableCell>
              <TruxTableCell>
                Entered
              </TruxTableCell>
              <TruxTableCell>
                Days Dormant
              </TruxTableCell>
              <TruxTableCell align="right">
                Location Last Reported
              </TruxTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredList.map((device, index) => {
              let deviceType = "";
              if (device.deviceType.startsWith("ELD Truck")) {
                deviceType = "Truck";
              } else if ((device.externalIdentifier || "").startsWith("1|")) {
                deviceType = "CT Trailer";
              } else if ((device.externalIdentifier || "").startsWith("2|")) {
                deviceType = "Samsara";
              } else if ((device.externalIdentifier || "").startsWith("3|")) {
                deviceType = "SkyBitz";
              } else if ((device.externalIdentifier || "").startsWith("4|")) {
                deviceType = "Premier";
              } else if ((device.externalIdentifier || "").startsWith("5|")) {
                deviceType = "CT Imobilizer";
              }
              return <TableRow key={index}>
                <TruxTableCell>
                  {deviceType}
                </TruxTableCell>
                <TruxTableCell>
                  {device.deviceType}
                </TruxTableCell>
                <TruxTableCell>
                  {device.assetNotification && formatDatetimeForPreview(device.assetNotification.notificationTime, timezone)}
                </TruxTableCell>
                <TruxTableCell>
                  {device.assetNotification && <>{dayjs().diff(dayjs(device.assetNotification.notificationTime), 'day')} Days</>}
                </TruxTableCell>
                <TruxTableCell align="right">
                  {formatDatetimeForPreview(device.lastPing, timezone)}
                </TruxTableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  devices?: DeviceWithLastAssetNotification[],
  eldLandmark: EldLandmark,
}

export default function Page() {
  const settings = useSettingsContext();
  const { eldLandmarkId } = useParams();

  const dataLoader = useCallback(() => ({
    devices: axios.get(
      formatEndpoint(endpoints.devices.findAllByLandmark, {eldLandmarkId})
    ).then(response => response.data),
    eldLandmark: axios.get(
      formatEndpoint(endpoints.eldLandmarks.findById, {id: eldLandmarkId})
    ).then(response => response.data),
  }), [eldLandmarkId]);

  const {
    data,
    updateData,
    setData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const router = useRouter();


  const historyButton = <Button
    onClick={() => {
      router.push(`/app/eld/eld-assets-per-landmark/history/${data?.eldLandmark.id}`)
    }}
    variant="outlined"
    color="secondary"
    startIcon={<Iconify icon="material-symbols:history-rounded" />}
  >
    History
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {data && <PageHeader title={`Assets currently in '${data.eldLandmark.name}'`}>
          {historyButton}
        </PageHeader>}

        {data && data?.devices?.length === 0 &&
          <NoneFound title={`No Devices Found in ${data.eldLandmark.name}`} message={`When devices enter ${data.eldLandmark.name} they will appear here.`} />
        }

        {data && data?.devices?.length !== 0 && <EldDevices devices={data?.devices} />}
      </DisplayPageData>
    </Container>
  );
}
